import { useEffect, useState } from 'react';
import { DefaultValues, UseFormReturn } from 'react-hook-form';
import { DataListItem, Form } from '@brighte/form-components';
import { ApplicationInputType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { useStepperActions, useStepperActiveIndex } from '@provider';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Columns } from '@spark-web/columns';
import { ChevronDownIcon, ChevronUpIcon, PencilIcon } from '@spark-web/icon';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useDisclosure } from '@spark-web/utils';
import { Step } from '@spark-web/vertical-stepper';
import { APPLICATION_STEPS, KNOWN_ERROR_MESSAGES, PASS_THROUGH_ERROR_MESSAGES, getErrorMessage } from '@utils';
import { ExplainErrorButton, ValidationFailureJournal, useAssistantSafe } from '../Assistant';
import { Loader } from '../Loader/Loader';
import ScrollIntoView from '../ScrollStepIntoView/ScrollStepIntoView';
import StepSummary from './StepSummary';
import { useSaveStepDetails } from './hooks';

type Props<TForm extends ApplicationInputType> = {
  children: (formProps: UseFormReturn<TForm>) => React.ReactNode;
  schema: any;
  name: string;
  title: string;
  isLoading?: boolean;
  defaultValues: DefaultValues<TForm>;
  isEditable?: boolean;
  completedContent?: Array<{ title: string; items: DataListItem[] | [] }> | [];
  disableNavigation?: boolean;
  shouldUnregister?: boolean;
  hideSubmitButton?: boolean;
  hideStepNumber?: boolean;
  description?: string;
  continueLabel?: string;
  showFormSummary?: boolean;
  footerContent?: (formProps: UseFormReturn<TForm>) => React.ReactNode;
  onBeforeNextStep?: (formValues: ApplicationInputType) => boolean;
  onValidateBeforeSave?: (formValues: ApplicationInputType) => boolean;
};

function StepNavigator<TForm extends ApplicationInputType>({
  children,
  schema,
  name,
  title,
  isLoading = false,
  isEditable = true,
  defaultValues,
  completedContent,
  disableNavigation = false,
  shouldUnregister = true,
  hideSubmitButton = false,
  hideStepNumber = false,
  description,
  continueLabel = 'Save and Continue',
  showFormSummary = false,
  footerContent,
  onBeforeNextStep,
  onValidateBeforeSave,
}: Props<TForm>) {
  const { onEdit, onNext } = useStepperActions();
  const activeIndex = useStepperActiveIndex();
  // Sentence case, so the button reads "Edit household details" rather than "Edit Household details".
  // Only the first character is lowered, leaving any later capitalisation in the step title intact.
  const editLabel = `Edit ${title.charAt(0).toLowerCase()}${title.slice(1)}`;
  const [loading, setLoading] = useState(isLoading);
  const { isOpen, onOpen, onToggle } = useDisclosure();
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);
  // Expanded state is keyed by step index so each completed step's summary toggles independently.
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const { saveStep } = useSaveStepDetails();
  const [error, setError] = useState<string | null>(null);
  const assistant = useAssistantSafe();

  const toggleStepExpanded = (stepIndex: number) =>
    setExpandedSteps(current => {
      const next = new Set(current);
      if (next.has(stepIndex)) {
        next.delete(stepIndex);
      } else {
        next.add(stepIndex);
      }
      return next;
    });

  const edit = (stepIndex: number) => {
    setSelectedStepIndex(stepIndex);
    onOpen();
  };

  const handleSubmit = async (values: ApplicationInputType) => {
    // true = blocked; caller has already set the form errors — clear any stale backend error and
    // don't run saveStep.
    if (onValidateBeforeSave && onValidateBeforeSave(values)) {
      setError(null);
      return;
    }

    setLoading(true);
    try {
      datadogRum.addAction('Before calling save step', {
        name,
        values,
      });
      const { isStepSaved, message } = await saveStep({ [name]: values });
      setLoading(false);

      // if onBeforeNextStep returns true, it means the user wants to stop the navigation and trigger callback
      if (onBeforeNextStep && onBeforeNextStep(values)) {
        return;
      }

      if (isStepSaved) {
        // If the current step is the finance purchase, always navigate to the next step(Eligibility)
        if (name === APPLICATION_STEPS.FINANCE_PURCHASE) {
          datadogRum.addAction('Submitted finance step');
          onNext(activeIndex + 1);
        } else {
          datadogRum.addAction(`Submitted ${name} step`);
          onNext();
        }
        setError(null);
      } else {
        const actionName = message
          ? `Failed to save ${name} step`
          : `Failed to save ${name} step - missing application payload`;
        datadogRum.addAction(actionName, { message: message ?? 'Failed to save step' });
        // Surface known backend validation messages (e.g. joint applicant uniqueness) verbatim; otherwise generic.
        const passThroughMessage = message
          ? PASS_THROUGH_ERROR_MESSAGES.find(knownMessage => message.includes(knownMessage))
          : undefined;
        setError(passThroughMessage ?? KNOWN_ERROR_MESSAGES.UPDATE_APPLICATION_ERROR);
        assistant?.recordEvent({ type: 'save_error', stepKey: name, errorCode: 'UPDATE_APPLICATION_ERROR' });
        setLoading(false);
      }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : getErrorMessage(error);
      datadogRum.addAction(`Failed to save ${name} step`, { message: errorMessage });
      setError(errorMessage);
      assistant?.recordEvent({ type: 'save_error', stepKey: name, errorCode: 'UPDATE_APPLICATION_ERROR' });
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  return (
    <>
      <Step>
        <Step.Heading
          title={title}
          renderActionButton={({ isCompleted, stepIndex }) => (
            <>
              {/* Skip the first step so its scroll doesn't push the page header off-screen on load. */}
              <ScrollIntoView enabled={stepIndex === activeIndex && stepIndex !== 0} />
              {isCompleted && (
                // Single toggle button so only one `aria-expanded` is ever in the DOM ("View" + chevron).
                <Button
                  type="button"
                  prominence="none"
                  tone="primary"
                  aria-expanded={expandedSteps.has(stepIndex)}
                  onClick={() => toggleStepExpanded(stepIndex)}
                >
                  View
                  {expandedSteps.has(stepIndex) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Button>
              )}
            </>
          )}
          renderStepNumber={hideStepNumber ? () => '' : undefined}
        />

        {description && (
          <Step.Description>
            <Box paddingBottom="small">
              <Text tone="muted" size="small">
                {description}
              </Text>
            </Box>
          </Step.Description>
        )}
        <Step.Content>
          {isLoading ? <Loader /> : null}

          {/* displays form summary with continue button */}
          {!isLoading && showFormSummary ? (
            <Stack gap="xsmall" paddingBottom="xlarge">
              <StepSummary completedContent={completedContent} />
              <Button disabled={disableNavigation} onClick={() => onNext()}>
                {continueLabel}
              </Button>
            </Stack>
          ) : null}

          {/* displays form */}
          {!isLoading && !showFormSummary ? (
            <Stack>
              <Form<TForm>
                onSubmit={handleSubmit}
                schema={schema}
                defaultValues={defaultValues}
                options={{ shouldUnregister }}
              >
                {({ ...formProps }: UseFormReturn<TForm>) => {
                  return (
                    <Stack gap="xxlarge" paddingBottom="xlarge">
                      <ValidationFailureJournal formState={formProps.formState} stepKey={name} />
                      {children(formProps)}
                      {error && (
                        <Stack gap="small">
                          <Text tone="critical">{error}</Text>
                          <ExplainErrorButton errorMessage={error} displayMessage={error} stepKey={name} />
                        </Stack>
                      )}
                      {!hideSubmitButton && (
                        <Button type="submit" disabled={disableNavigation} loading={loading}>
                          {continueLabel}
                        </Button>
                      )}
                      {footerContent ? <Box>{footerContent(formProps)}</Box> : null}
                    </Stack>
                  );
                }}
              </Form>
            </Stack>
          ) : null}
        </Step.Content>

        <Step.CompletedContent
          render={({ stepIndex }) =>
            expandedSteps.has(stepIndex) ? (
              // paddingBottom separates the Edit button from the next step, making clear which step it edits.
              <Stack gap="large" paddingBottom="large">
                <StepSummary completedContent={completedContent} />
                {isEditable && (
                  <Button type="button" prominence="low" tone="primary" onClick={() => edit(stepIndex)}>
                    <PencilIcon />
                    {editLabel}
                  </Button>
                )}
              </Stack>
            ) : (
              // Render prop is typed to return a ReactElement, so an empty fragment (not null) is required here.
              <></>
            )
          }
        />
      </Step>
      <ContentDialog title="Unsaved changes" isOpen={isOpen} onToggle={onToggle}>
        <Text>Are you sure you want to leave this section? Your changes will be lost if you proceed.</Text>
        <Columns gap="xxlarge">
          <Button
            onClick={() => {
              onToggle();
              onEdit(selectedStepIndex);
            }}
          >
            Yes, proceed
          </Button>
          <Button prominence="low" onClick={onToggle}>
            Cancel
          </Button>
        </Columns>
      </ContentDialog>
    </>
  );
}

export { StepNavigator };
